<script setup lang="ts">
/**
 * "ลงออเดอร์": saves a real order into the POS (beta for now) through /api/v1/pos.
 * Prices/shipping come from the POS (live quote); staff may override the shipping fee with a reason;
 * discounts only by coupon code; bundle/กับข้าว stay on the POS sale page.
 * "ยังไม่โอน · ส่ง QR ให้ลูกค้า" (owner 18 ต.ค. 69): the POS holds the bill until "ยืนยันชำระ" (order/HeldOrderCard.vue).
 * A chat with a held order shows it first — no new-order form, nothing filled into one — until "ลงออเดอร์ใหม่อีกใบ (ลูกค้า
 * สั่งเพิ่ม)" (owner 18 ต.ค. 69 "แค่รีเฟสระบบก็ขึ้นมาให้ลงออเดอร์ใหม่ได้ อยากให้ดูบริบทด้วยว่าลูกค้ารอโอน"); "แก้ไขออเดอร์" opens this
 * form prefilled and saves into the SAME held bill (customer and payment locked).
 * "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า" (owner 18 ต.ค. 69): the POS keeps a card payment link the customer pays by themselves; the POS
 * then makes the order (order/CardLinkCard.vue). Shown first like a held order; "แก้ไขออเดอร์" cancels the old link and makes a
 * new one. A chat never has a held QR order and a card link open at once (the server refuses: 409 payRequestOpen).
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ConversationDetail } from '../../api/types'
import { baht, clockTime, dayKey } from '../../lib/inbox'
import {
  bangkokTime,
  billText,
  CARD_CONTINUE_TEXT,
  CARD_TOO_LATE_TEXT,
  cardExpiry,
  cardTooLate,
  slipPayment,
  counts,
  deliveryDateLabel,
  feeOverrideValue,
  OVERRIDE_REASON_MIN,
  dateChips,
  dayNotes,
  deliveryOf,
  isLocked,
  LOCKED_TEXT,
  PENDING_CONTINUE_TEXT,
  PIN_REQUIRED_TEXT,
  slotLabel,
  type OrderForm,
} from '../../lib/order'
import { useClock } from '../../stores/clock'
import { useInbox } from '../../stores/inbox'
import { useOrders } from '../../stores/order'
import { useScript } from '../../stores/script'
import { useMenuRead } from '../../stores/menuRead'
import { useSession } from '../../stores/session'
import LibDialog from '../library/LibDialog.vue'
import Icon from '../ui/Icon.vue'
import ChatReadBar from './order/ChatReadBar.vue'
import CustomerBlock from './order/CustomerBlock.vue'
import CardLinkCard from './order/CardLinkCard.vue'
import HeldOrderCard from './order/HeldOrderCard.vue'
import MenuPicker from './order/MenuPicker.vue'
import '../../styles/order.css'

const props = defineProps<{ detail: ConversationDetail }>()
const inbox = useInbox()
const orders = useOrders()
const script = useScript()
const clock = useClock()
const menuRead = useMenuRead()
const session = useSession()

const id = computed(() => props.detail.id)
// ขยาย → ย่อกลับ rebuilds this tab: keep the scroll of the form and of the menu list (stores/menuRead.ts)
const body = ref<HTMLElement | null>(null)
function expandMenus(): void {
  const el = body.value
  menuRead.expand(id.value, { top: el?.scrollTop ?? 0, list: el?.querySelector<HTMLElement>('.ml')?.scrollTop ?? 0 })
}
onMounted(() => {
  const at = menuRead.takeSideScroll(id.value)
  if (!at) return
  let tries = 0
  // the form may still be filling in (customer card, packs): try a few frames until it is tall enough
  const go = (): void => {
    const el = body.value
    if (!el) return
    el.scrollTop = at.top
    const list = el.querySelector<HTMLElement>('.ml')
    if (list) list.scrollTop = at.list
    if ((el.scrollTop < at.top - 2 || (list && list.scrollTop < at.list - 2)) && tries++ < 30) requestAnimationFrame(go)
  }
  void nextTick(go)
})
watch(
  id,
  (v) => {
    orders.form(v)
    // no pack yet: the one the chat chose / the system recommends (stores/script.ts suggestPack)
    void orders.open(v).then(() => (id.value === v ? script.suggestPack(v) : false))
  },
  { immediate: true },
)

const f = computed(() => orders.forms[id.value] ?? null)
const ctx = computed(() => orders.contexts[id.value] ?? null)
const cat = computed(() => orders.catalog)
const q = computed(() => orders.quoteOf(id.value))
const quote = computed(() => q.value.data)
const result = computed(() => orders.results[id.value] ?? null)
const saving = computed(() => !!orders.saving[id.value])
const issues = computed(() => orders.issues(id.value))
const saveError = computed(() => orders.saveErrors[id.value] ?? null)
const retrying = computed(() => !!orders.pendingClicks[id.value])
/** A save of this chat the POS has not answered yet: the server keeps asking; no new order until it ends. */
const open = computed(() => orders.openOrders[id.value] ?? null)
const checkingNow = computed(() => !!orders.checking[id.value] || !!open.value?.checking)
const openPack = computed(() => {
  const s = open.value?.summary
  if (!s) return ''
  if (s.continue_header_id) return 'สั่งต่อแพ็กเดิม'
  return cat.value?.set_menus.find((m) => m.id === s.set_menu_id)?.name ?? 'แพ็ก'
})
const openWhen = computed(() => {
  const s = open.value?.summary
  if (!s?.delivery_date) return '—'
  return `${deliveryDateLabel(s.delivery_date)} ${s.shipping === 'dhl' ? 'DHL' : (slotLabel(cat.value, s.delivery_time) ?? '')}`.trim()
})
const openPay = computed(() => {
  const s = open.value?.summary
  if (!s) return '—'
  if (s.payment_method === 'CashOnDelivery') return 'เก็บเงินปลายทาง'
  if (s.payment_method === 'PendingTransfer') return 'ยังไม่โอน (พักบิลรอโอน)'
  if (s.payment_method === 'CardLink') return 'บัตรเครดิต (ส่งลิงก์ชำระ)'
  return s.amount ? `ลูกค้าโอนแล้ว ${baht(s.amount)}` : 'ลูกค้าโอนแล้ว'
})
const openChecks = computed(() => {
  const o = open.value
  if (!o) return ''
  const parts = [o.checks ? `ถาม POS ซ้ำแล้ว ${o.checks} ครั้ง` : 'จะถาม POS ซ้ำในไม่กี่วินาที']
  if (o.checked_at) parts.push(`ล่าสุด ${clockTime(o.checked_at)}`)
  if (o.next_check_at && !o.checking && !o.auto_stopped) parts.push(`ครั้งถัดไป ${clockTime(o.next_check_at)}`)
  return parts.join(' · ')
})
/** "ยกเลิกคำขอนี้": owner/head only, once the server stopped asking by itself (the API refuses everyone else). */
const canAbandon = computed(() => {
  const role = session.me?.role
  return !!open.value?.auto_stopped && (role === 'owner' || role === 'head')
})
const abandonOpen = ref(false)
const abandonBusy = ref(false)
const abandonError = ref<string | null>(null)
function askAbandon(): void {
  abandonError.value = null
  abandonOpen.value = true
}
function closeAbandon(): void {
  if (!abandonBusy.value) abandonOpen.value = false
}
async function confirmAbandon(): Promise<void> {
  if (abandonBusy.value) return
  abandonBusy.value = true
  abandonError.value = null
  try {
    await orders.abandon(id.value)
    abandonOpen.value = false
    inbox.flash('ยกเลิกคำขอเดิมแล้ว ลงออเดอร์ใหม่ในแชทนี้ได้')
  } catch (e) {
    abandonError.value = e instanceof Error ? e.message : String(e)
    void orders.refreshContext(id.value) // it may have ended meanwhile (done / being asked)
  } finally {
    abandonBusy.value = false
  }
}

const history = computed(() => (f.value?.customerId ? (orders.histories[f.value.customerId]?.data ?? null) : null))
const openPacks = computed(() => history.value?.open_packs ?? [])
const pack = computed(() => cat.value?.set_menus.find((s) => s.id === f.value?.setMenuId) ?? null)
const contPack = computed(() => openPacks.value.find((p) => p.order_header_id === f.value?.continueHeaderId) ?? null)
const quota = computed(() => (contPack.value ? contPack.value.meals_remaining : (pack.value?.meals ?? 0)))
const cnt = computed(() => (f.value ? counts(f.value, cat.value) : { meals: 0, snacks: 0 }))
const over = computed(() => Math.max(0, cnt.value.meals - quota.value))

const chLabel = computed(() => ({ fb: 'FB', ig: 'IG', line: 'LINE', web: 'เว็บ' })[props.detail.channel])
const posChannel = computed(() => {
  const c = ctx.value?.channel
  if (!c) return null
  const type = { facebook: 'Facebook', line: 'LINE', instagram: 'Instagram' }[c.type] ?? c.type
  return `${type} · ${c.name}`
})
const who = computed(() => {
  const cid = f.value?.customerId
  const c = cid ? orders.customers[cid] : null
  return c?.name ?? props.detail.contact.name
})
const whoPhone = computed(() => {
  const cid = f.value?.customerId
  return (cid ? orders.customers[cid]?.mobile_masked : null) ?? props.detail.contact.phone_masked
})

const delivery = computed(() => deliveryOf(cat.value, clock.now))
const slots = computed(() => delivery.value.slots)
const chips = computed(() => (f.value ? dateChips(delivery.value, f.value.shipping) : []))
const anyLocked = computed(() => chips.value.some((c) => c.locked))
const anyOff = computed(() => chips.value.some((c) => c.off))
const dateLocked = computed(() => !!f.value && isLocked(delivery.value, f.value.date, f.value.shipping))
const dateNotes = computed(() => (f.value ? dayNotes(delivery.value, f.value.date, f.value.shipping) : []))
/** Picked with "วันอื่น" (not one of the quick days). */
const dateFar = computed(() => !!f.value && !chips.value.some((c) => c.date === f.value?.date))
const feeSystem = computed(() => quote.value?.shipping.fee_system ?? null)
/** Lalamove for a POS customer without a pin: not saved until the pin is set in section 1 (owner 18 ต.ค. 69) — the manual
 *  fee is not offered as the way out any more. */
const needPin = computed(() => {
  const cid = f.value?.customerId
  return f.value?.shipping === 'lalamove' && !!cid && orders.customers[cid]?.has_pin === false
})
const feeOv = computed(() => (f.value ? feeOverrideValue(f.value) : null))
const reasonShort = computed(() => !!f.value?.feeOverride.on && f.value.feeOverride.reason.trim().length < OVERRIDE_REASON_MIN)
const transferDiff = computed(() => {
  if (!f.value || f.value.pay.method !== 'BankTransfer' || !quote.value || !f.value.pay.amount.trim()) return 0
  return Number(f.value.pay.amount) - quote.value.totals.total
})
const couponAllowed = computed(() => !f.value?.continueHeaderId)
const quoteBusy = computed(() => q.value.state === 'loading')
const shownTotal = computed(() => (quote.value && q.value.fresh ? quote.value.totals.total : (quote.value?.totals.total ?? null)))

function set<K extends keyof OrderForm>(k: K, v: OrderForm[K]): void {
  orders.update(id.value, { [k]: v } as Partial<OrderForm>)
}
function setPay(patch: Partial<OrderForm['pay']>): void {
  if (!f.value) return
  // the staff typed a date / time / amount: the slip never overwrites it
  const typed = 'date' in patch || 'time' in patch || 'amount' in patch
  orders.update(id.value, { pay: { ...f.value.pay, ...patch, ...(typed ? { from: 'staff' as const } : {}) } })
}
/** The customer's newest checked slip in this chat (lib/order.ts slipPayment): "ลูกค้าโอนแล้ว" takes its date, time and
 *  amount — unless the staff typed their own (owner 18 ต.ค. 69 "วันและเวลาอ้างอิงจากสลิปที่โอน"). */
const slipPay = computed(() => slipPayment(inbox.threads[id.value] ?? []))
watch(
  () => [f.value?.pay.method, f.value?.pay.from, slipPay.value] as const,
  ([method, from, s]) => {
    if (!f.value || method !== 'BankTransfer' || from === 'staff' || !s) return
    const p = f.value.pay
    if (p.from === 'slip' && p.date === s.date && p.time === s.time && p.amount === s.amount) return
    orders.update(id.value, { pay: { ...p, date: s.date, time: s.time, amount: s.amount, from: 'slip' } })
  },
  { immediate: true },
)
function setOv(patch: Partial<OrderForm['feeOverride']>): void {
  if (!f.value) return
  const next = { ...f.value.feeOverride, ...patch }
  if (patch.on && !f.value.feeOverride.fee && feeSystem.value !== null) next.fee = String(feeSystem.value)
  orders.update(id.value, { feeOverride: next })
}
function onPack(e: Event): void {
  const v = (e.target as HTMLSelectElement).value
  const keep = f.value?.source?.kind === 'repeat' || f.value?.source?.kind === 'chat'
  orders.update(id.value, { setMenuId: v ? Number(v) : null, continueHeaderId: null, source: keep ? (f.value?.source ?? null) : null })
}
function clearMenus(): void {
  orders.update(id.value, { items: [] })
}
function clearCart(): void {
  orders.startNew(id.value)
}
async function save(): Promise<void> {
  const ed = editRef.value
  if (ed) {
    if (!(await orders.saveEdit(id.value))) return
    const now = orders.heldOrders(id.value).find((o) => o.id === ed.id) ?? orders.results[id.value]?.chat_order
    const t = now?.id === ed.id ? now.total : null
    if (ed.card) {
      inbox.flash(`บันทึกการแก้ไขแล้ว ลิงก์เดิมถูกยกเลิก ได้ลิงก์ใหม่${t !== null ? ` ยอด ${baht(t)}` : ''} ส่งลิงก์ใหม่ให้ลูกค้า`)
      return
    }
    inbox.flash(
      t !== null && t !== ed.total
        ? `บันทึกการแก้ไขพักบิลแล้ว ยอดใหม่ ${baht(t)} ส่งใบสรุปยอดและ QR ใหม่ให้ลูกค้า`
        : 'บันทึกการแก้ไขพักบิลแล้ว (ยอดเท่าเดิม)',
    )
    return
  }
  const ok = await orders.save(id.value)
  if (ok) {
    const co = result.value?.chat_order
    inbox.flash(co?.status !== 'held' ? 'บันทึกออเดอร์ลง POS แล้ว' : co.card ? 'สร้างลิงก์ชำระบัตรแล้ว กด "ส่งลิงก์ให้ลูกค้า"' : 'พักบิลรอโอนใน POS แล้ว')
  }
}
function sendBill(): void {
  const r = result.value
  if (!r) return
  // an untouched AI draft is replaced; anything the staff typed stays above the summary
  orders.fillComposer(id.value, billText(r, cat.value))
  inbox.flash('ใส่ใบสรุปยอดในช่องพิมพ์แล้ว ตรวจแล้วกดส่ง')
}

// ------------------------------------------------ "ยังไม่โอน · ส่ง QR ให้ลูกค้า" (held bills) · "บัตรเครดิต · ส่งลิงก์ให้ลูกค้า"
const pending = computed(() => f.value?.pay.method === 'PendingTransfer')
const cardPay = computed(() => f.value?.pay.method === 'CardLink')
/** The POS takes card links (chat_v1 with CardLink in catalog.payment_methods). */
const cardOk = computed(() => !!cat.value?.payment_methods.includes('CardLink'))
/** "ชำระได้ถึง จ. 4 ต.ค. 21:30 น." of a link for the picked day (21:30 the day before delivery). */
const cardUntil = computed(() => {
  if (!f.value) return ''
  const t = cardExpiry(f.value.date)
  return Number.isNaN(t) ? '' : `${deliveryDateLabel(dayKey(t))} ${bangkokTime(t)} น.`
})
const resultHeld = computed(() => result.value?.chat_order.status === 'held')
const resultVoid = computed(() => result.value?.chat_order.status === 'void')
const resultCard = computed(() => !!result.value?.chat_order.card)
/** The chat's other orders waiting for the transfer (the saved one is shown on its own). */
const held = computed(() => orders.heldOrders(id.value).filter((o) => o.id !== result.value?.chat_order.id))
/** "แก้ไขออเดอร์": the held order this form edits (its hold code stays). */
const editRef = computed(() => orders.editing[id.value] ?? null)
/** Someone saved another edit of it meanwhile (the save would be refused): reopen to start from it. */
const editStale = computed(() => {
  const e = editRef.value
  const now = e ? orders.heldOrders(id.value).find((o) => o.id === e.id) : null
  const n = (o: typeof e) => o?.hold?.edit_count ?? o?.card?.edit_count ?? 0
  return !!e && !!now && n(now) > n(e)
})
/** The held orders shown first are card links / QR bills (the gate's words). */
const heldCards = computed(() => held.value.filter((o) => !!o.card).length)
/** What the tab shows: the edit form · a saved order · a save waiting for the POS · the held order(s) first · the form. */
const screen = computed(() => {
  if (!f.value) return 'none'
  if (editRef.value) return 'edit'
  if (result.value) return resultHeld.value ? 'held' : resultVoid.value ? 'void' : 'done'
  if (open.value) return 'open'
  if (orders.heldGate(id.value)) return 'gate'
  return 'form'
})
// while one waits for the transfer: ask every 60 s (paid on the POS sale page / removed there → the tab shows it)
let poll: ReturnType<typeof setInterval> | null = null
watch(
  () => resultHeld.value || held.value.length > 0,
  (on) => {
    if (on && !poll) poll = setInterval(() => void orders.refreshContext(id.value), 60000)
    if (!on && poll) {
      clearInterval(poll)
      poll = null
    }
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  if (poll) clearInterval(poll)
})
</script>

<template>
  <div class="pane ord" role="tabpanel" aria-label="ลงออเดอร์">
    <!-- ================================================= saved: held (waiting for the transfer) -->
    <div v-if="screen === 'held' && result && f && resultCard" class="op-body scroll">
      <div class="done">
        <span class="done-ic card-ic" aria-hidden="true"><Icon name="creditCard" /></span>
        <h3 class="done-t">สร้างลิงก์ชำระบัตรแล้ว</h3>
        <p class="note">ยังไม่ออกใบ ยังไม่ตัดสต๊อก · กด "ส่งลิงก์ให้ลูกค้า" ใส่ใบสรุปยอดและลิงก์ในช่องพิมพ์ · ลูกค้าจ่ายแล้ว POS ออกใบให้เอง</p>
        <CardLinkCard class="done-held" :conv-id="id" :order="result.chat_order" :result="result" saved />
        <ul v-if="result.order.warnings?.length" class="done-w">
          <li v-for="w in result.order.warnings" :key="w"><Icon name="info" />{{ w }}</li>
        </ul>
        <button class="tlink ac" @click="orders.openNewOrder(id)"><Icon name="plus" />ลงออเดอร์ใหม่อีกใบ (ลูกค้าสั่งเพิ่ม)</button>
      </div>
    </div>

    <div v-else-if="screen === 'held' && result && f" class="op-body scroll">
      <div class="done">
        <span class="done-ic hold-ic" aria-hidden="true"><Icon name="clock" /></span>
        <h3 class="done-t">พักบิลรอโอนใน POS แล้ว</h3>
        <p class="note">ยังไม่ออกใบ ยังไม่ตัดสต๊อก · ส่งใบสรุปยอดพร้อม QR พร้อมเพย์ให้ลูกค้า แล้วกด "ยืนยันชำระ" เมื่อได้สลิป</p>
        <HeldOrderCard class="done-held" :conv-id="id" :order="result.chat_order" :result="result" saved />
        <ul v-if="result.order.warnings?.length" class="done-w">
          <li v-for="w in result.order.warnings" :key="w"><Icon name="info" />{{ w }}</li>
        </ul>
        <p class="note">หน้าขาย POS เห็นในรายการบิลที่พัก ถ้าเปิดบิลที่พักแล้วบันทึกที่หน้าขาย แชทจะขึ้นว่าชำระแล้วเอง</p>
        <button class="tlink ac" @click="orders.openNewOrder(id)"><Icon name="plus" />ลงออเดอร์ใหม่อีกใบ (ลูกค้าสั่งเพิ่ม)</button>
      </div>
    </div>

    <!-- ================================================= saved: the held bill was removed in the POS -->
    <div v-else-if="screen === 'void' && result && f" class="op-body scroll">
      <div class="done">
        <span class="done-ic wait-ic" aria-hidden="true"><Icon name="x" /></span>
        <h3 class="done-t">{{
          result.chat_order.card ? 'ยกเลิกลิงก์ชำระบัตรแล้ว' : result.chat_order.error_message?.startsWith('ยกเลิกพักบิล') ? 'ยกเลิกพักบิลแล้ว' : 'พักบิลนี้ถูกลบที่ POS แล้ว'
        }}</h3>
        <p class="note">{{ result.chat_order.error_message || 'ไม่มีออเดอร์เกิดขึ้น ถ้าลูกค้ายังต้องการ ลงออเดอร์ใหม่ได้เลย' }}</p>
        <button class="tlink ac" @click="clearCart"><Icon name="plus" />ลงออเดอร์ใหม่</button>
      </div>
    </div>

    <!-- ================================================= saved -->
    <div v-else-if="screen === 'done' && result && f" class="op-body scroll">
      <div class="done">
        <span class="done-ic" aria-hidden="true"><Icon name="checkCircle" /></span>
        <h3 class="done-t">{{
          result.chat_order.paid?.via === 'card' ? 'ลูกค้าชำระผ่านลิงก์บัตรแล้ว ออกใบใน POS แล้ว' : result.chat_order.paid ? 'ยืนยันชำระแล้ว ออกใบใน POS แล้ว' : 'บันทึกลง POS แล้ว'
        }}</h3>
        <p class="done-code mono">{{ result.chat_order.order_code }}</p>
        <p v-if="result.chat_order.paid?.via === 'pos'" class="note">หน้าขาย POS เปิดบิลที่พักแล้วบันทึก</p>
        <p v-else-if="result.chat_order.paid?.via === 'card'" class="note">
          ลูกค้าจ่ายบัตรผ่านลิงก์<template v-if="result.chat_order.paid.amount !== null"> {{ baht(result.chat_order.paid.amount) }}</template> · POS ออกใบให้เอง
        </p>
        <p v-else-if="result.chat_order.paid" class="note">
          ยืนยันชำระโดย {{ result.chat_order.paid.by_name ?? 'ทีม' }}<template v-if="result.chat_order.paid.amount !== null">
            · ยอดโอน {{ baht(result.chat_order.paid.amount) }}</template
          >
        </p>
        <p v-if="result.followed_up" class="note">POS ยืนยันภายหลัง ระบบถามซ้ำด้วยคำขอเดิมให้ จึงไม่ได้ลงซ้ำ</p>
        <p v-else-if="result.replayed" class="note">ออเดอร์นี้บันทึกไว้แล้วจากการกดครั้งก่อน ระบบไม่ได้ลงซ้ำ</p>
        <div v-if="result.order.order" class="done-kv">
          <div class="kv"><span>แพ็ก</span><b>{{ result.order.order.set_menu.name }} · {{ result.order.order.meals }} มื้อ</b></div>
          <div class="kv">
            <span>จัดส่ง</span
            ><b
              >{{ deliveryDateLabel(result.order.order.delivery.date) }}
              {{ result.order.order.shipping.carrier === 'dhl' ? 'DHL' : slotLabel(cat, result.order.order.delivery.time) }}</b
            >
          </div>
          <div class="kv"><span>ยอดรวม</span><b class="mono">{{ baht(result.order.order.totals.total) }}</b></div>
        </div>
        <ul v-if="result.order.warnings?.length" class="done-w">
          <li v-for="w in result.order.warnings" :key="w"><Icon name="info" />{{ w }}</li>
        </ul>
        <div class="done-acts">
          <button class="btn-save" @click="sendBill"><Icon name="send" />ส่งใบสรุปยอดในแชท</button>
          <a v-if="result.chat_order.bill_url" class="btn2" :href="result.chat_order.bill_url" target="_blank" rel="noopener noreferrer"
            ><Icon name="external" />เปิดบิล</a
          >
        </div>
        <p class="note">ปุ่มนี้ใส่ข้อความพร้อมลิงก์บิลในช่องพิมพ์ ตรวจแล้วกดส่งเอง</p>
        <button class="tlink ac" @click="clearCart"><Icon name="plus" />ลงออเดอร์ใหม่</button>
      </div>
    </div>

    <!-- ================================================= waiting for the POS (api/app/pos_followup.py) -->
    <div v-else-if="screen === 'open' && open && f" class="op-body scroll">
      <div class="done" role="status" aria-live="polite">
        <span class="done-ic wait-ic" aria-hidden="true"><span v-if="checkingNow" class="spin"></span><Icon v-else name="clock" /></span>
        <h3 class="done-t">รอผลจาก POS</h3>
        <p class="note">
          ออเดอร์ที่บันทึก{{ open.created_at ? ` เมื่อ ${clockTime(open.created_at)} น.` : '' }} POS ยังไม่ยืนยันผล
          {{ open.auto_stopped ? 'การถามซ้ำใช้คำขอเดิมเสมอ จึงไม่ลงซ้ำ' : 'ระบบกำลังถาม POS ซ้ำให้อัตโนมัติด้วยคำขอเดิม จึงไม่ลงซ้ำ' }}
        </p>
        <div class="done-kv">
          <div class="kv"><span>แพ็ก</span><b>{{ openPack }} · {{ open.summary.meals }} มื้อ</b></div>
          <div class="kv"><span>จัดส่ง</span><b>{{ openWhen }}</b></div>
          <div class="kv"><span>ชำระ</span><b>{{ openPay }}</b></div>
          <div class="kv"><span>บันทึกโดย</span><b>{{ open.staff_name ?? 'ทีม' }}</b></div>
        </div>
        <p class="soon wait-lock"><Icon name="lock" /><span>ลงออเดอร์ใหม่ในแชทนี้ได้เมื่อรู้ผลแล้ว (กันลงซ้ำ)</span></p>
        <p class="note">{{ checkingNow ? 'กำลังถาม POS…' : openChecks }}</p>
        <p v-if="open.auto_stopped" class="bn crit wait-bn">
          <Icon name="alertCircle" /><span
            >ครบ 30 นาทีแล้ว ระบบหยุดถาม POS อัตโนมัติและแจ้งผู้ดูแลระบบแล้ว · กด "ตรวจผลตอนนี้" ได้ตลอด หรือตรวจใน POS</span
          >
        </p>
        <div class="done-acts">
          <button class="btn2" :disabled="checkingNow" @click="orders.checkNow(id)"><Icon name="refresh" />ตรวจผลตอนนี้</button>
          <button v-if="canAbandon" class="btn2 wait-x" :disabled="checkingNow" @click="askAbandon"><Icon name="x" />ยกเลิกคำขอนี้</button>
        </div>
        <p v-if="saveError" class="err" role="alert"><Icon name="alertCircle" /><span>{{ saveError }}</span></p>
      </div>
      <LibDialog
        v-if="abandonOpen"
        title="ยกเลิกคำขอนี้"
        :sub="`ออเดอร์ที่บันทึก${open.created_at ? ` เมื่อ ${clockTime(open.created_at)} น.` : ''} โดย ${open.staff_name ?? 'ทีม'}`"
        :width="460"
        :close-on-backdrop="!abandonBusy"
        @close="closeAbandon"
      >
        <p class="ab-q">ตรวจใน POS แล้วว่าไม่มีออเดอร์นี้ ยกเลิกคำขอนี้และให้ลงออเดอร์ใหม่ในแชทนี้ได้</p>
        <ul class="ab-list">
          <li><Icon name="receipt" /><span>{{ openPack }} · {{ open.summary.meals }} มื้อ · {{ openWhen }} · {{ openPay }}</span></li>
          <li><Icon name="lock" /><span>ยกเลิกแล้ว ระบบจะไม่ส่งคำขอนี้ไป POS อีก และแชทนี้ลงออเดอร์ใหม่ได้</span></li>
          <li class="ab-warn"><Icon name="alert" /><span>ถ้าออเดอร์นี้อยู่ใน POS แล้ว อย่ายกเลิก ให้กด "ตรวจผลตอนนี้" แทน</span></li>
        </ul>
        <p v-if="abandonError" class="ab-err" role="alert"><Icon name="alertCircle" /><span>{{ abandonError }}</span></p>
        <template #footer>
          <span class="grow"></span>
          <button type="button" class="btn" autofocus :disabled="abandonBusy" @click="closeAbandon">ไม่ยกเลิก</button>
          <button type="button" class="btn ab-go" :disabled="abandonBusy" @click="confirmAbandon">
            <Icon name="x" />{{ abandonBusy ? 'กำลังยกเลิก…' : 'ไม่มีใน POS ยกเลิกคำขอนี้' }}
          </button>
        </template>
      </LibDialog>
    </div>

    <!-- ================================================= the chat's held order(s) first: no new form, nothing filled -->
    <div v-else-if="screen === 'gate' && f" class="op-body scroll">
      <section class="held-main" aria-label="ออเดอร์รอชำระของแชทนี้">
        <div class="hm-h">
          <span class="done-ic hold-ic hm-ic" aria-hidden="true"><Icon :name="heldCards === held.length ? 'creditCard' : 'clock'" /></span>
          <div class="hm-tt">
            <h3 class="hm-t">
              ลูกค้ารอชำระ {{ held.length }} ออเดอร์ ({{
                heldCards === held.length ? 'ส่งลิงก์ชำระบัตร' : heldCards ? 'ลิงก์บัตร / พักบิลไว้ใน POS' : 'พักบิลไว้ใน POS'
              }})
            </h3>
            <p v-if="heldCards === held.length" class="note">ยังไม่ออกใบ ยังไม่ตัดสต๊อก · ลูกค้าจ่ายผ่านลิงก์แล้ว POS ออกใบให้เอง · ลูกค้าขอเปลี่ยน กด "แก้ไขออเดอร์" · เปลี่ยนวิธีชำระ กด "ยกเลิกลิงก์" ก่อน</p>
            <p v-else class="note">ยังไม่ออกใบ ยังไม่ตัดสต๊อก · ส่งใบสรุปยอดพร้อม QR ให้ลูกค้า แล้วกด "ยืนยันชำระ" เมื่อได้สลิป · ลูกค้าขอเปลี่ยน กด "แก้ไขออเดอร์"</p>
          </div>
        </div>
        <template v-for="o in held" :key="o.id">
          <CardLinkCard v-if="o.card" :conv-id="id" :order="o" saved />
          <HeldOrderCard v-else :conv-id="id" :order="o" saved />
        </template>
        <p v-if="ctx?.staff_error" class="bn crit"><Icon name="alertCircle" /><span>{{ ctx.staff_error.message }}</span></p>
        <button class="tlink hm-new" @click="orders.openNewOrder(id)"><Icon name="plus" />ลงออเดอร์ใหม่อีกใบ (ลูกค้าสั่งเพิ่ม)</button>
      </section>
    </div>

    <!-- ================================================= form (a new order · "แก้ไขออเดอร์" of a held one) -->
    <div v-else-if="f" ref="body" class="op-body scroll">
      <div v-if="editRef" class="edit-h" role="group" :aria-label="editRef.card ? 'แก้ไขลิงก์ชำระบัตร' : 'แก้ไขพักบิล'">
        <div class="edit-t">
          <Icon name="pencil" /><b>{{ editRef.card ? `แก้ไขลิงก์ชำระบัตร ${editRef.card.code ?? ''}` : `แก้ไขพักบิล ${editRef.hold?.code ?? ''}` }}</b
          ><span class="grow"></span>
          <button class="tlink" :disabled="saving" @click="orders.endEdit(id)"><Icon name="x" />ยกเลิกการแก้ไข</button>
        </div>
        <p v-if="editRef.card" class="note">
          บันทึกแล้วลิงก์เดิมถูกยกเลิกทันที (ลูกค้าจ่ายลิงก์เดิมไม่ได้อีก) และได้ลิงก์ใหม่ตามยอดใหม่ · ลูกค้าและวิธีชำระล็อกไว้ · ยอดเดิม
          {{ baht(editRef.total ?? 0) }}
        </p>
        <p v-else class="note">แก้ในพักบิลเดิมของ POS (รหัสเดิม ไม่ลงใบใหม่) · ลูกค้าและวิธีชำระล็อกไว้ · ยอดเดิม {{ baht(editRef.total ?? 0) }}</p>
        <p v-if="editStale" class="bn crit">
          <Icon name="alertCircle" /><span>มีคนแก้ออเดอร์นี้ไปแล้ว กดยกเลิกการแก้ไขแล้วเปิดแก้ไขใหม่ เพื่อแก้จากข้อมูลล่าสุด</span>
        </p>
      </div>
      <div class="who">
        <div class="who-1">
          <span class="who-l">ลงให้</span>
          <span class="cus-n">{{ who }}</span>
          <span v-if="whoPhone" class="mono">{{ whoPhone }}</span>
          <span class="grow"></span>
          <span v-if="f.customerId" class="okc"><Icon name="check" />ผูก POS แล้ว</span>
          <span v-else class="okc none">ยังไม่ผูก POS</span>
        </div>
        <div class="who-2">
          <span class="lockc" :aria-label="`ช่องทางขาย ${posChannel ?? chLabel} ตั้งอัตโนมัติ แก้ไม่ได้`"
            ><Icon name="lock" />{{ posChannel ?? chLabel }}</span
          >
          <span>อัตโนมัติ</span>
        </div>
        <div v-if="ctx && (ctx.env !== 'root' || ctx.staff?.source === 'fallback')" class="who-2">
          <span v-if="ctx.env !== 'root'" class="chip warn">ลงที่ POS {{ ctx.env }}</span>
          <span v-if="ctx.staff?.source === 'fallback'">ในชื่อพนักงาน POS ทดสอบ #{{ ctx.staff.pos_staff_id }}</span>
        </div>
        <div v-if="f.source" class="legend">
          <Icon :name="f.source.kind === 'chat' ? 'checks' : 'repeat'" />
          <span>{{ f.source.label }} · ตรวจก่อนบันทึก</span>
          <span class="grow"></span>
          <button class="tlink" @click="clearCart"><Icon name="trash" />ล้างตะกร้า</button>
        </div>
        <ul v-if="f.source?.notes?.length" class="src-notes" aria-label="ข้อควรเช็คจากแชท">
          <li v-for="n in f.source.notes" :key="n"><Icon name="info" /><span>{{ n }}</span></li>
        </ul>
      </div>
      <!-- "เติมจากแชททั้งหมด": the AI reads the whole chat into the empty fields (stores/script.ts readChat) -->
      <ChatReadBar v-if="!editRef" :conv-id="id" />

      <section v-if="held.length && !editRef" class="held-list" aria-label="ออเดอร์รอชำระของแชทนี้">
        <div class="held-h">
          <Icon name="clock" /><span>รอชำระ {{ held.length }} ออเดอร์{{ heldCards ? ' · ลูกค้าเปลี่ยนวิธีชำระ ยกเลิกลิงก์ก่อน (กันจ่ายซ้ำ)' : ' (พักบิลไว้ใน POS)' }}</span>
        </div>
        <template v-for="o in held" :key="o.id">
          <CardLinkCard v-if="o.card" :conv-id="id" :order="o" />
          <HeldOrderCard v-else :conv-id="id" :order="o" />
        </template>
      </section>

      <p v-if="orders.contextErrors[id]" class="bn crit"><Icon name="alertCircle" /><span>{{ orders.contextErrors[id] }}</span></p>
      <p v-if="ctx?.staff_error" class="bn crit"><Icon name="alertCircle" /><span>{{ ctx.staff_error.message }}</span></p>
      <p v-if="ctx?.channel_error" class="bn crit"><Icon name="alertCircle" /><span>{{ ctx.channel_error.message }}</span></p>
      <p v-if="orders.catalogError && !cat" class="bn crit"><Icon name="alertCircle" /><span>โหลดแพ็กและเมนูจาก POS ไม่สำเร็จ: {{ orders.catalogError }}</span></p>
      <p v-else-if="cat?.stale" class="bn"><Icon name="info" /><span>POS ไม่ตอบ ใช้รายการแพ็กและเมนูชุดล่าสุดที่โหลดไว้</span></p>

      <!-- 1 customer -->
      <CustomerBlock :conv-id="id" :contact-name="detail.contact.name" :locked="!!editRef" />

      <!-- 2 pack + menu -->
      <section class="os" aria-label="แพ็กและเมนู">
        <div class="os-h">
          <span class="os-n">2</span><h3 class="os-t">แพ็กและเมนู</h3><span class="grow"></span>
          <span v-if="pack || contPack" class="cnt" :class="{ warn: over > 0 }">{{ cnt.meals }}/{{ quota }} มื้อ</span>
        </div>
        <div v-if="openPacks.length && !f.continueHeaderId && !editRef" class="opk">
          <span class="sub-l">แพ็กที่ยังเหลือสิทธิ์ของลูกค้าคนนี้</span>
          <button v-for="p in openPacks" :key="p.order_header_id" class="opk-b" @click="orders.startContinue(id, p)">
            <Icon name="repeat" /><span class="opk-t">สั่งต่อ {{ p.set_menu_name }}</span><span class="cnt mut">เหลือ {{ p.meals_remaining }} มื้อ</span>
          </button>
        </div>
        <div v-if="contPack" class="opk on">
          <span class="opk-b is-on"
            ><Icon name="repeat" /><span class="opk-t">สั่งต่อ {{ contPack.set_menu_name }}</span
            ><span class="cnt">เหลือ {{ contPack.meals_remaining }}/{{ contPack.meals_quota }} มื้อ</span></span
          >
          <button class="tlink" @click="orders.stopContinue(id)"><Icon name="x" />เลิกสั่งต่อ ใช้แพ็กใหม่</button>
          <p class="note">สั่งต่อไม่คิดราคาแพ็ก คิดเฉพาะเมนูเพิ่มและมื้อที่เกินสิทธิ์ที่เหลือ</p>
        </div>
        <select
          v-else
          class="sel"
          :class="{ 'is-on': !!pack }"
          :value="f.setMenuId ?? ''"
          aria-label="เลือกแพ็ก"
          :disabled="!cat"
          @change="onPack"
        >
          <option value="">เลือกแพ็ก…</option>
          <option v-for="s in cat?.set_menus ?? []" :key="s.id" :value="s.id">{{ s.name }} — {{ baht(s.price) }} · {{ s.meals }} มื้อ</option>
        </select>
        <p v-if="f.source?.kind === 'repeat' && !pack && !contPack" class="note">แพ็กของออเดอร์เดิมปิดขายแล้ว เลือกแพ็กใหม่</p>
        <template v-if="pack || contPack">
          <div class="mn-h">
            <span class="sub-l">เมนู</span>
            <span v-if="over > 0" class="chip warn">เกินแพ็ก {{ over }} กล่อง คิดราคาเมนู</span>
            <span v-else-if="cnt.meals < quota" class="chip">ขาดอีก {{ quota - cnt.meals }} มื้อ</span>
            <span v-if="cnt.snacks" class="chip">ของว่าง {{ cnt.snacks }}</span>
            <span class="grow"></span>
            <button v-if="f.items.length" class="tlink" @click="clearMenus"><Icon name="trash" />ล้างเมนู</button>
            <button class="tlink ac" title="เปิดเมนูทั้งหมดพร้อมรูป ครึ่งจอข้างแชท" @click="expandMenus"><Icon name="maximize" />ขยาย</button>
          </div>
          <MenuPicker :conv-id="id" />
        </template>
        <p class="note">bundle และกับข้าว ลงที่หน้าขาย POS</p>
      </section>

      <!-- 3 delivery -->
      <section class="os" aria-label="จัดส่ง">
        <div class="os-h">
          <span class="os-n">3</span><h3 class="os-t">จัดส่ง</h3><span class="grow"></span
          ><span class="os-s"><Icon name="calendar" /><b>{{ deliveryDateLabel(f.date) }}</b></span>
        </div>
        <div class="seg" role="radiogroup" aria-label="ขนส่ง">
          <button class="seg-b" :class="{ 'is-on': f.shipping === 'lalamove' }" role="radio" :aria-checked="f.shipping === 'lalamove'" @click="set('shipping', 'lalamove')">
            <Icon name="truck" />Lalamove
          </button>
          <button class="seg-b" :class="{ 'is-on': f.shipping === 'dhl' }" role="radio" :aria-checked="f.shipping === 'dhl'" @click="set('shipping', 'dhl')">
            <Icon name="globe" />DHL ต่างจังหวัด
          </button>
        </div>
        <p v-if="needPin" class="err"><Icon name="alertCircle" /><span>{{ PIN_REQUIRED_TEXT }}</span></p>
        <div class="fl">
          <span class="fl-l">วันส่ง</span>
          <div class="dchips" role="radiogroup" aria-label="วันส่ง 14 วันข้างหน้า">
            <button
              v-for="c in chips"
              :key="c.date"
              class="dchip"
              :class="{ 'is-on': f.date === c.date, locked: c.locked, off: c.off }"
              role="radio"
              :aria-checked="f.date === c.date"
              :aria-label="`${deliveryDateLabel(c.date)}${c.locked ? ' ปิดรับแล้ว' : ''}${c.notes.length ? ` · ${c.notes.join(' · ')} เลือกไม่ได้` : ''}`"
              :title="c.locked ? 'ทีมยิงงาน Lalamove ของวันนี้ไปแล้ว' : c.notes.join(' · ') || undefined"
              :disabled="c.locked || c.off"
              @click="set('date', c.date)"
            >
              <span class="dc-t">{{ c.top }}</span><span class="dc-d mono">{{ c.day }}</span>
            </button>
          </div>
          <div class="dmore">
            <span class="sub-l">วันอื่น</span>
            <label class="inp" :class="{ 'is-on': dateFar, bad: dateLocked }"
              ><Icon name="calendar" /><input
                type="date"
                :value="f.date"
                :min="delivery.date_min"
                :max="delivery.date_max"
                aria-label="เลือกวันส่งอื่น"
                @change="set('date', ($event.target as HTMLInputElement).value || f.date)"
            /></label>
            <span v-if="anyLocked || anyOff" class="sub-l dleg">วันที่กดไม่ได้ = ปิดรับแล้ว หรือวันหยุด</span>
          </div>
          <p v-if="dateLocked" class="err"><Icon name="alertCircle" /><span>{{ LOCKED_TEXT }}</span></p>
          <p v-else-if="dateNotes.length" class="err"><Icon name="alertCircle" /><span>{{ dateNotes.join(' · ') }} เลือกวันอื่น</span></p>
        </div>
        <template v-if="f.shipping === 'lalamove'">
          <div class="sub-l">รอบส่ง</div>
          <div class="slots" role="radiogroup" aria-label="รอบส่ง">
            <button
              v-for="s in slots"
              :key="s.time"
              class="slot"
              :class="{ 'is-on': f.time === s.time }"
              role="radio"
              :aria-checked="f.time === s.time"
              @click="set('time', s.time)"
            >
              {{ s.label }}
            </button>
          </div>
        </template>
        <p v-else class="note">DHL ไม่มีรอบเวลา ส่งตามรอบรถ</p>

        <div class="dl">
          <span class="muted">ค่าส่ง</span>
          <template v-if="quote">
            <span v-if="feeOv !== null && feeSystem !== null && feeOv !== feeSystem" class="fee old">{{ baht(feeSystem) }}</span>
            <span class="fee">{{ baht(quote.shipping.fee) }}</span>
            <span class="dotsep"></span>
            <span class="muted">{{ feeOv !== null ? 'แก้โดยพนักงาน' : feeSystem === null ? 'ระบบคิดไม่ได้' : 'ระบบคิด' }}</span>
            <span v-if="quote.shipping.reason" class="muted">· {{ quote.shipping.reason }}</span>
          </template>
          <span v-else class="muted">คิดเมื่อเลือกลูกค้า แพ็ก และเมนูแล้ว</span>
          <span class="grow"></span>
          <button v-if="!f.feeOverride.on" class="tlink ac" :disabled="!quote" @click="setOv({ on: true })"><Icon name="pencil" />แก้ค่าส่ง</button>
        </div>
        <p v-if="quote && feeSystem === null && !f.feeOverride.on && !needPin" class="err">
          <Icon name="alertCircle" /><span>ระบบคิดค่าส่งไม่ได้ (เช่นหมุดอยู่นอกโซน Lalamove) กด "แก้ค่าส่ง" แล้วใส่ค่าส่งพร้อมเหตุผล</span>
        </p>
        <div v-if="f.feeOverride.on" class="ovr">
          <div class="ovr-h"><Icon name="pencil" />แก้ค่าส่ง (ต้องมีเหตุผล)<span class="grow"></span
            ><button class="tlink" @click="setOv({ on: false, fee: '', reason: '' })">ใช้ค่าระบบ</button></div>
          <div class="ovr-g">
            <label class="inp" :class="{ bad: f.feeOverride.fee !== '' && feeOv === null }"
              ><input
                :value="f.feeOverride.fee"
                type="text"
                inputmode="numeric"
                aria-label="ค่าส่งที่แก้ บาท"
                @input="setOv({ fee: ($event.target as HTMLInputElement).value })" /><span class="unit">บาท</span></label
            >
            <label class="inp" :class="{ bad: reasonShort && f.feeOverride.reason !== '' }"
              ><input
                :value="f.feeOverride.reason"
                type="text"
                maxlength="255"
                aria-label="เหตุผลที่แก้ค่าส่ง"
                placeholder="เหตุผล เช่น ลูกค้าประจำ"
                @input="setOv({ reason: ($event.target as HTMLInputElement).value })"
            /></label>
          </div>
        </div>
        <div v-if="quote?.warnings.length" class="qnote" role="note" aria-label="ข้อควรเช็คจาก POS">
          <div class="qnote-h"><Icon name="info" />ข้อควรเช็ค ระบบไม่บังคับ</div>
          <ul>
            <li v-for="w in quote.warnings" :key="w">{{ w }}</li>
          </ul>
        </div>
      </section>

      <!-- 4 coupon + note -->
      <section class="os" aria-label="คูปองและหมายเหตุ">
        <div class="os-h"><span class="os-n">4</span><h3 class="os-t">คูปองและหมายเหตุ</h3></div>
        <div class="cprow">
          <label class="inp"
            ><Icon name="ticket" /><input
              :value="f.couponCode"
              type="text"
              aria-label="โค้ดคูปอง"
              :placeholder="couponAllowed ? 'โค้ดคูปอง' : 'สั่งต่อใช้คูปองไม่ได้'"
              :disabled="!couponAllowed"
              @input="set('couponCode', ($event.target as HTMLInputElement).value)"
              @keydown.enter.prevent="orders.applyCoupon(id)"
          /></label>
          <button
            v-if="f.coupon.state !== 'ok'"
            class="btn-s"
            :disabled="!couponAllowed || !f.couponCode.trim() || f.coupon.state === 'checking' || !f.customerId || !f.setMenuId"
            @click="orders.applyCoupon(id)"
          >
            <span v-if="f.coupon.state === 'checking'" class="spin"></span>ใช้โค้ด
          </button>
          <button v-else class="btn-s" @click="orders.clearCoupon(id)"><Icon name="x" />เอาออก</button>
        </div>
        <div v-if="f.coupon.state === 'ok'" class="cpst">
          <span class="chip ok"><Icon name="check" />{{ f.coupon.code }} · {{ f.coupon.label }}</span>
          <span class="mono">−{{ baht(f.coupon.discount) }}</span>
          <span class="note">คิดจากยอดตอนกรอกโค้ด เหมือนหน้าขาย</span>
        </div>
        <div v-else-if="f.coupon.state === 'stale'" class="cpst">
          <span class="chip warn"><Icon name="alert" />{{ f.coupon.code }} ต้องตรวจใหม่</span>
          <span class="note">เปลี่ยนแพ็กหลังกรอกโค้ด ส่วนลดเดิมใช้ไม่ได้</span>
          <button class="tlink ac" @click="orders.applyCoupon(id)">ตรวจโค้ดอีกครั้ง</button>
        </div>
        <p v-else-if="f.coupon.state === 'error'" class="err"><Icon name="alertCircle" /><span>{{ f.coupon.message }}</span></p>
        <p v-else-if="!f.setMenuId && couponAllowed" class="note">เลือกแพ็กก่อนใส่โค้ด</p>
        <label class="inp"
          ><Icon name="pencil" /><input
            :value="f.remark"
            type="text"
            maxlength="255"
            aria-label="หมายเหตุถึงครัว ไม่บังคับ"
            placeholder="หมายเหตุถึงครัว (ไม่บังคับ)"
            @input="set('remark', ($event.target as HTMLInputElement).value)"
        /></label>
      </section>

      <!-- 5 payment -->
      <section class="os last" aria-label="ชำระเงิน">
        <div class="os-h"><span class="os-n">5</span><h3 class="os-t">ชำระเงิน</h3></div>
        <div v-if="editRef?.card" class="pay-lock">
          <span class="lockc" aria-label="วิธีชำระ บัตรเครดิต ล็อกไว้"><Icon name="lock" />บัตรเครดิต · ส่งลิงก์ให้ลูกค้า</span>
          <p class="note">เปลี่ยนวิธีชำระไม่ได้ (ยกเลิกลิงก์ที่การ์ดก่อนแล้วลงใหม่) · บันทึกแล้วต้องส่งลิงก์ใหม่ให้ลูกค้า ลิงก์เดิมใช้ไม่ได้แล้ว</p>
        </div>
        <div v-else-if="editRef" class="pay-lock">
          <span class="lockc" aria-label="วิธีชำระ ยังไม่โอน ล็อกไว้"><Icon name="lock" />ยังไม่โอน · ส่ง QR ให้ลูกค้า</span>
          <p class="note">พักบิลรอโอน เปลี่ยนวิธีชำระไม่ได้ (ได้สลิปแล้วใช้ "ยืนยันชำระ" ที่การ์ด) · บันทึกแล้วต้องส่ง QR ใหม่ถ้ายอดเปลี่ยน</p>
        </div>
        <div v-else class="pay" role="radiogroup" aria-label="วิธีชำระเงิน">
          <button
            class="po"
            :class="{ 'is-on': f.pay.method === 'BankTransfer' }"
            role="radio"
            :aria-checked="f.pay.method === 'BankTransfer'"
            @click="setPay({ method: 'BankTransfer' })"
          >
            <span class="radio" :class="{ on: f.pay.method === 'BankTransfer' }"></span
            ><span class="po-b"><span class="po-t">ลูกค้าโอนแล้ว</span><span class="po-s">ใส่เวลาและยอดตามสลิป</span></span>
          </button>
          <button
            class="po"
            :class="{ 'is-on': f.pay.method === 'CashOnDelivery' }"
            role="radio"
            :aria-checked="f.pay.method === 'CashOnDelivery'"
            @click="setPay({ method: 'CashOnDelivery' })"
          >
            <span class="radio" :class="{ on: f.pay.method === 'CashOnDelivery' }"></span
            ><span class="po-b"><span class="po-t">เก็บเงินปลายทาง</span><span class="po-s">ค่าธรรมเนียม +{{ baht(cat?.cod_fee ?? 30) }}</span></span>
          </button>
          <button
            class="po po-x"
            :class="{ 'is-on': pending }"
            role="radio"
            :aria-checked="pending"
            @click="setPay({ method: 'PendingTransfer' })"
          >
            <span class="radio" :class="{ on: pending }"></span
            ><span class="po-b"
              ><span class="po-t">ยังไม่โอน · ส่ง QR</span
              ><span class="po-s">พักบิลไว้ รอลูกค้าโอน</span></span
            >
          </button>
          <button
            class="po po-x"
            :class="{ 'is-on': cardPay }"
            role="radio"
            :aria-checked="cardPay"
            :disabled="!cardOk"
            :title="cardOk ? undefined : 'POS รุ่นนี้ยังสร้างลิงก์ชำระบัตรจากแชทไม่ได้'"
            @click="setPay({ method: 'CardLink' })"
          >
            <span class="radio" :class="{ on: cardPay }"></span
            ><span class="po-b"
              ><span class="po-t">บัตรเครดิต</span
              ><span class="po-s">{{ cardOk ? 'ส่งลิงก์ · ชำระได้ถึง 21:30 วันก่อนส่ง' : 'POS รุ่นนี้ยังไม่รองรับ' }}</span></span
            >
          </button>
        </div>
        <p v-if="pending && f.continueHeaderId" class="err"><Icon name="alertCircle" /><span>{{ PENDING_CONTINUE_TEXT }}</span></p>
        <template v-if="cardPay && !editRef">
          <p v-if="f.continueHeaderId" class="err"><Icon name="alertCircle" /><span>{{ CARD_CONTINUE_TEXT }}</span></p>
          <p v-else-if="cardTooLate(f.date, clock.now)" class="err"><Icon name="alertCircle" /><span>{{ CARD_TOO_LATE_TEXT }}</span></p>
          <p v-else class="note">
            บันทึกแล้วยังไม่ออกใบ ไม่ตัดสต๊อก (ลิงก์ชำระบัตรในหน้าขาย POS) · ได้ลิงก์ยอด{{ shownTotal !== null ? ` ${baht(shownTotal)}` : '' }}
            ชำระได้ถึง {{ cardUntil }} · ส่วนลดต้องใส่โค้ดก่อนสร้างลิงก์ (ลูกค้าใส่เองที่ลิงก์ไม่ได้)
          </p>
          <p v-if="ctx && ctx.env === 'beta'" class="bn crit"><Icon name="alertCircle" /><span>ลิงก์ beta ยังต่อ Beam จริง ห้ามจ่ายจนตั้งโหมดทดสอบ (เห็นเฉพาะทีม)</span></p>
        </template>
        <p v-else-if="pending && !editRef" class="note">
          บันทึกแล้วยังไม่ออกใบ ไม่ตัดสต๊อก (พักบิลในหน้าขาย POS) · กด "ส่งใบสรุปยอดและ QR ในแชท" ได้ QR พร้อมเพย์ตามยอด{{
            shownTotal !== null ? ` ${baht(shownTotal)}` : ''
          }}</p
        >
        <div v-if="f.pay.method === 'BankTransfer'" class="grid3">
          <label class="fl"
            ><span class="fl-l">วันที่โอน</span
            ><span class="inp"
              ><input type="date" :value="f.pay.date" aria-label="วันที่โอน" @change="setPay({ date: ($event.target as HTMLInputElement).value })" /></span
          ></label>
          <label class="fl"
            ><span class="fl-l">เวลา</span
            ><span class="inp"
              ><input type="time" :value="f.pay.time" aria-label="เวลาที่โอน" @change="setPay({ time: ($event.target as HTMLInputElement).value })" /></span
          ></label>
          <label class="fl"
            ><span class="fl-l">ยอดโอน</span
            ><span class="inp"
              ><input
                :value="f.pay.amount"
                type="text"
                inputmode="decimal"
                aria-label="ยอดโอน บาท"
                :placeholder="shownTotal !== null ? String(shownTotal) : 'ตามยอด'"
                @input="setPay({ amount: ($event.target as HTMLInputElement).value })" /></span
          ></label>
        </div>
        <p v-if="transferDiff !== 0" class="err">
          <Icon name="alertCircle" /><span>ยอดโอน{{ transferDiff > 0 ? 'เกิน' : 'ขาด' }}ยอดออเดอร์ {{ baht(Math.abs(transferDiff)) }} ตรวจสลิปอีกครั้ง</span>
        </p>
        <p v-if="f.pay.method === 'BankTransfer' && f.pay.from === 'slip' && slipPay" class="note">
          วัน เวลา และยอดจากสลิปที่ลูกค้าส่งในแชท ({{ slipPay.label }}<template v-if="slipPay.bank"> · {{ slipPay.bank }}</template>)
        </p>
        <p v-if="f.pay.method === 'BankTransfer'" class="note">เว้นยอดโอนไว้ = เท่ายอดออเดอร์</p>
      </section>
    </div>

    <!-- ================================================= footer -->
    <footer v-if="f && (screen === 'form' || screen === 'edit')" class="op-foot">
      <div class="sum">
        <div class="sum-l">
          <div class="sl">
            <span>{{ contPack ? 'สั่งต่อ' : pack ? pack.name : 'แพ็ก' }}{{ quote && quote.totals.plus + quote.totals.overset > 0 ? ' + เมนูเพิ่ม' : '' }}</span
            ><span class="mono">{{ quote ? baht(quote.totals.subtotal) : '—' }}</span>
          </div>
          <div class="sl"><span>ค่าส่ง</span><span class="mono">{{ quote ? baht(quote.totals.shipping_fee) : '—' }}</span></div>
          <div class="sl">
            <span>ส่วนลด</span><span class="mono" :class="{ neg: !!quote?.totals.discount }">{{ quote?.totals.discount ? `−${baht(quote.totals.discount)}` : '—' }}</span>
          </div>
          <div v-if="quote?.totals.cod_fee" class="sl"><span>เก็บเงินปลายทาง</span><span class="mono">{{ baht(quote.totals.cod_fee) }}</span></div>
          <div v-if="editRef" class="sl"><span>{{ editRef.card ? 'ยอดเดิมของลิงก์' : 'ยอดเดิมของพักบิล' }}</span><span class="mono">{{ baht(editRef.total ?? 0) }}</span></div>
        </div>
        <div class="sum-r">
          <span class="sum-rl"><span v-if="quoteBusy" class="spin" aria-label="กำลังคำนวณ"></span>รวมทั้งหมด</span>
          <span class="sum-rv" :class="{ dim: !q.fresh }">{{ shownTotal !== null ? baht(shownTotal) : '—' }}</span>
        </div>
      </div>
      <div class="acts">
        <button v-if="editRef" class="btn2" :disabled="saving" @click="orders.endEdit(id)">ยกเลิก</button>
        <button class="btn-save" :disabled="saving || issues.length > 0" aria-describedby="ord-why" @click="save">
          <span v-if="saving" class="spin"></span><Icon v-else name="save" />{{
            saving
              ? 'กำลังบันทึก…'
              : editRef
                ? editRef.card
                  ? 'บันทึกการแก้ไข (ลิงก์ใหม่)'
                  : 'บันทึกการแก้ไข'
                : retrying
                  ? 'บันทึกอีกครั้ง'
                  : pending
                    ? 'พักบิลรอโอนลง POS'
                    : cardPay
                      ? 'สร้างลิงก์ชำระบัตร'
                      : 'บันทึกออเดอร์ลง POS'
          }}
        </button>
      </div>
      <p v-if="saveError" id="ord-why" class="soon bad" role="alert"><Icon name="alertCircle" /><span>{{ saveError }}</span></p>
      <p v-else-if="issues.length" id="ord-why" class="soon"><Icon name="info" /><span>{{ issues[0] }}</span></p>
      <p v-else id="ord-why" class="soon">
        <Icon name="info" /><span>{{
          editRef
            ? editRef.card
              ? 'ราคาและค่าส่งคิดโดย POS · บันทึกแล้วลิงก์เดิมถูกยกเลิก ได้ลิงก์ใหม่ ส่งลิงก์ใหม่ให้ลูกค้า'
              : 'ราคาและค่าส่งคิดโดย POS · บันทึกแล้วแก้ในพักบิลเดิม ถ้ายอดเปลี่ยนต้องส่งใบสรุปยอดและ QR ใหม่ให้ลูกค้า'
            : pending
              ? 'ราคาและค่าส่งคิดโดย POS · พักบิลแล้วส่งใบสรุปยอดพร้อม QR ในแชทได้'
              : cardPay
                ? 'ราคาและค่าส่งคิดโดย POS · ได้ลิงก์แล้วกด "ส่งลิงก์ให้ลูกค้า" ที่การ์ด'
                : 'ราคาและค่าส่งคิดโดย POS · บันทึกแล้วส่งใบสรุปยอดในแชทได้'
        }}</span>
      </p>
    </footer>
    <p v-if="!f" class="pane-note">กำลังเตรียมฟอร์ม…</p>
  </div>
</template>

<style scoped>
.opk {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.opk-b {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 6px 10px;
  border: 1px dashed #bfdccd;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--ac2);
}
.opk-b:hover {
  background: var(--acs);
}
.opk-b.is-on {
  border-style: solid;
  border-color: var(--ac);
  background: #f5faf7;
  box-shadow: inset 0 0 0 1px var(--ac);
}
.opk-b .ic {
  width: 15px;
  height: 15px;
  flex: none;
}
.opk-t {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}
.opk.on {
  gap: 4px;
}
.opk.on .tlink {
  align-self: flex-start;
}
.mn-h {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-height: 26px;
  margin-top: 4px;
}
.grid3 {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.9fr) minmax(0, 1fr);
  gap: 8px;
}
.dchips {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
}
.dchip {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding: 4px 0 5px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  color: #3e4d46;
  min-width: 0;
}
.dchip:hover:not(:disabled) {
  background: #f5faf7;
}
.dc-t {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.dc-d {
  font-size: 13.5px;
  line-height: 20px;
  font-weight: 600;
}
.dchip.is-on {
  border-color: var(--ac);
  background: #f5faf7;
  box-shadow: inset 0 0 0 1px var(--ac);
  color: var(--ac2);
}
.dchip.is-on .dc-t {
  color: var(--ac2);
}
.dchip.off::after {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #d9a441;
}
/* a day off (holiday / weekly day off / web closed): cannot be picked, the dot says why on hover */
.dchip.off {
  background: #f4f6f5;
  color: var(--mut, #8a958f);
  cursor: not-allowed;
}
.dchip.locked {
  background: #f4f6f5;
  border-style: dashed;
  color: #9aa7a1;
}
.dchip.locked .dc-t {
  color: #9aa7a1;
}
.dchip.locked .dc-d {
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
}
.dmore {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.dmore .inp {
  width: 168px;
  height: 32px;
}
.dmore .inp.is-on {
  border-color: var(--ac);
  box-shadow: inset 0 0 0 1px var(--ac);
}
.dleg {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.qnote {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f7f9f8;
}
.qnote-h {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: var(--mut);
}
.qnote-h .ic {
  width: 14px;
  height: 14px;
  color: var(--mut);
}
.qnote ul {
  margin: 0;
  padding: 0 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.qnote li {
  font-size: 12.5px;
  line-height: 19px;
  color: #3e4d46;
}
.done-w {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.done-w li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: #6b4300;
}
.done-w .ic {
  width: 14px;
  height: 14px;
  flex: none;
  margin-top: 3px;
}
.done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 28px 32px;
  text-align: center;
}
.done-ic {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--acs);
  color: var(--ac);
}
.done-ic .ic {
  width: 30px;
  height: 30px;
}
.done-t {
  font-size: 17px;
  font-weight: 600;
  line-height: 26px;
}
.done-code {
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  letter-spacing: 0.3px;
}
.done-kv {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 12px;
  text-align: left;
}
.kv {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 14px;
  font-size: 13px;
  line-height: 20px;
}
.kv + .kv {
  border-top: 1px solid var(--line2);
}
.kv span {
  width: 56px;
  flex: none;
  color: var(--mut);
  font-size: 12.5px;
}
.kv b {
  font-weight: 600;
  min-width: 0;
}
.done-acts {
  align-self: stretch;
  display: flex;
  gap: 8px;
  margin-top: 6px;
}
.done-acts .btn2 {
  text-decoration: none;
}
.wait-ic {
  background: #fdf3e1;
  color: #8a5a00;
}
.hold-ic {
  background: #fdf3e1;
  color: #8a5a00;
}
.card-ic {
  background: #e2ebfb;
  color: #1d4385;
}
.done-held {
  align-self: stretch;
}
.held-main {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 20px 28px;
}
.hm-h {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.ord .hm-ic {
  flex: none;
  width: 40px;
  height: 40px;
}
.ord .hm-ic :deep(.ic) {
  width: 22px;
  height: 22px;
}
.hm-tt {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.hm-t {
  margin: 0;
  font-size: 15px;
  line-height: 23px;
  font-weight: 600;
  color: #6b4300;
}
.hm-tt .note {
  margin: 0;
}
.hm-new {
  align-self: flex-start;
  margin-top: 2px;
}
.edit-h {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 12px 20px 0;
  padding: 10px 12px;
  border: 1px solid #f0d9a8;
  border-radius: 12px;
  background: #fffaf0;
}
.edit-t {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 21px;
  color: #6b4300;
}
.edit-t :deep(.ic) {
  width: 15px;
  height: 15px;
  flex: none;
}
.edit-h .note,
.ord .edit-h .bn {
  margin: 0;
}
.pay-lock {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.pay-lock .note {
  margin: 0;
}
.held-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 20px 0;
}
.held-h {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  color: #6b4300;
}
.held-h :deep(.ic) {
  width: 14px;
  height: 14px;
}
/* QR and card share the second row (owner 18 ต.ค. 69 "qr กับ บัตร บรรทัดเดียวกัน") */
.po-x .po-b {
  flex: 1;
  min-width: 0;
}
.wait-ic .spin {
  width: 22px;
  height: 22px;
}
.wait-lock {
  justify-content: center;
}
.ord .wait-bn {
  align-self: stretch;
  margin: 0;
  text-align: left;
}
.wait-x:hover:not(:disabled) {
  color: var(--crit);
  border-color: var(--crit);
}
.ab-q {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: var(--ink);
}
.ab-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ab-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13.5px;
  line-height: 21px;
  color: var(--ink);
}
.ab-list :deep(.ic) {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 2.5px;
  color: var(--mut);
}
.ab-list li.ab-warn {
  color: var(--crit);
  font-weight: 600;
}
.ab-list li.ab-warn :deep(.ic) {
  color: var(--crit);
}
.ab-err {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--crits);
  color: var(--crit);
  font-size: 13px;
  line-height: 20px;
}
.ab-err :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
  margin-top: 2.5px;
}
.ab-go {
  background: var(--crit);
  border-color: var(--crit);
  color: #fff;
  font-weight: 600;
}
.ab-go :deep(.ic) {
  color: #fff;
}
.ab-go:hover:not(:disabled) {
  background: #a82217;
}
.ab-go:disabled,
.btn:disabled {
  opacity: 0.6;
}
</style>
